import React, { useEffect, useMemo, useState } from "react";
import styled, { keyframes } from "styled-components";
import { Link } from "react-router-dom";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { SG_GET_EMPLOYEE_ORG_CATEGORIES, SG_GET_SURVEY_STRUCTURES, SG_GET_EMPLOYEE_USER_CATEGORIES } from "constants/actions";

const DataAccess = () => {
  const dispatch = useDispatch();

  const {
    get_employees,
    get_auth,
    get_employee_org_category,
    get_survey_structure,
    core_data,
  } = useSelector(
    (state) => ({
      get_employees: state.employees,
      get_auth: state.auth,
      get_employee_org_category: state.employee_category?.employee_org_category,
      get_survey_structure: state.surveystructure?.survey_structure,
      core_data: state.audit?.core_data,
    }),
    shallowEqual
  );

  const isAdmin = get_employees?.userEmp?.account_type === 5;

  // Ensure categories and org categories are loaded
  useEffect(() => {
    if (get_auth?.organization_id) {
      if (!get_employee_org_category?.[0]) {
        dispatch({
          type: SG_GET_EMPLOYEE_ORG_CATEGORIES,
          payload: `organization=${get_auth?.organization_id}`,
        });
      }
      if (!get_survey_structure?.[0]) {
        dispatch({
          type: SG_GET_SURVEY_STRUCTURES,
          payload: `organization=${get_auth?.organization_id}`,
        });
      }
    }
  }, [dispatch, get_auth?.organization_id, get_employee_org_category, get_survey_structure]);

  // Ensure the current user's category permissions are loaded
  useEffect(() => {
    if (get_auth?.employee_id) {
      const employeeId = Number(get_auth.employee_id);
      dispatch({
        type: SG_GET_EMPLOYEE_USER_CATEGORIES,
        payload: `employee=${employeeId}`,
      });
    }
  }, [dispatch, get_auth?.employee_id]);

  // Active members (exclude archived, pending, deleted)
  const activeMembers = useMemo(() => {
    const members = get_employees?.employees?.filter(
      (e) => e.status !== 99 && e.status !== 98 && e.status !== 108
    ) || [];
    return members.sort((a, b) => (a.id > b.id ? 1 : -1));
  }, [get_employees?.employees]);

  // Visible categories (like ContactListV2)
  const visibleCategories = useMemo(() => {
    const categories = core_data?.categories?.[0]?.categories || get_survey_structure?.[0]?.categories;
    if (!categories) return [];
    return categories.filter((cat) => cat.visibility !== "private");
  }, [core_data, get_survey_structure]);

  // Default anchor = primary category if present; else first visible
  const defaultAnchor = useMemo(() => {
    const primary = visibleCategories.find((c) => c.priority === "primary");
    return primary || visibleCategories[0] || null;
  }, [visibleCategories]);

  const [selectedAnchorName, setSelectedAnchorName] = useState(defaultAnchor?.name || "");
  useEffect(() => {
    if (defaultAnchor?.name) setSelectedAnchorName(defaultAnchor.name);
  }, [defaultAnchor?.name]);

  const selectedAnchor = useMemo(() => {
    return visibleCategories.find((c) => c.name === selectedAnchorName) || null;
  }, [visibleCategories, selectedAnchorName]);

  // Build a lookup of employeeId -> categories array for fast access
  const employeeIdToCategories = useMemo(() => {
    const map = new Map();
    (get_employee_org_category || []).forEach((row) => {
      if (row?.employee) map.set(row.employee, row.category || []);
    });
    return map;
  }, [get_employee_org_category]);

  // Removed invalid hook usage inside useMemo; not needed

  // Better: read employee_user_category directly
  const get_employee_user_category = useSelector(
    (state) => state.employee_category?.employee_user_category,
    shallowEqual
  );

  const userAllowedPrimaryValueIds = useMemo(() => {
    if (isAdmin) return null; // null signifies all
    const currentUserCats = get_employee_user_category?.find(
      (f) => Number(f.employee_id) === Number(get_auth?.employee_id)
    );
    const ids = (currentUserCats?.category || [])
      .filter((c) => c?.priority === "primary")
      .map((c) => c?.value_id ?? c?.value)
      .filter(Boolean);
    // If no explicit primary permissions are set for the user,
    // default to full access (null), matching existing loader behavior.
    return ids.length > 0 ? ids : null;
  }, [get_employee_user_category, get_auth?.employee_id, isAdmin]);

  // Determine if an employee matches user's primary permissions
  const employeeMatchesUserAccess = (employeeId) => {
    if (isAdmin) return true;
    // If user has no explicit primary permissions, treat as full access
    if (userAllowedPrimaryValueIds === null) return true;
    const empCats = employeeIdToCategories.get(employeeId) || [];
    // Any match on primary by value_id or value
    return empCats.some((cat) => {
      if (cat?.priority !== "primary") return false;
      const vid = cat?.value_id ?? cat?.value;
      return userAllowedPrimaryValueIds.includes(vid);
    });
  };

  // Accessible employees list
  const accessibleEmployees = useMemo(() => {
    return activeMembers.filter((m) => employeeMatchesUserAccess(m.id));
  }, [activeMembers, userAllowedPrimaryValueIds, isAdmin, employeeIdToCategories]);

  const totalAccessible = accessibleEmployees.length;

  // Build options for the selected anchor (flatten subfactors if present for primary-like anchors)
  const anchorOptionItems = useMemo(() => {
    if (!selectedAnchor) return [];
    const opts = [];
    (selectedAnchor.options || []).forEach((opt) => {
      if (opt?.subfactors?.length > 0) {
        opt.subfactors.forEach((sf) => {
          opts.push({ id: sf.id, name: sf.name, parentId: opt.id, parentName: opt.name });
        });
      } else {
        opts.push({ id: opt.id, name: opt.name });
      }
    });
    return opts;
  }, [selectedAnchor]);

  // Get display value for an employee for the selected anchor
  const getEmployeeAnchorValue = (employeeId) => {
    const empCats = employeeIdToCategories.get(employeeId) || [];
    if (!selectedAnchor) return null;

    if (selectedAnchor.priority === "primary" || selectedAnchor.priority === "secondary") {
      // Match by value_id against options or subfactors
      const targetCat = empCats.find((c) => c.priority === selectedAnchor.priority);
      if (!targetCat) return null;
      const valueId = targetCat?.value_id ?? targetCat?.value;
      // Try direct option
      const option = (selectedAnchor.options || []).find((o) => o.id === valueId);
      if (option) return option.name;
      // Try subfactors
      for (const o of selectedAnchor.options || []) {
        const sf = (o.subfactors || []).find((s) => s.id === valueId);
        if (sf) return sf.name;
      }
      return null;
    }

    // Non-primary anchors often store name/value directly
    const direct = empCats.find((c) => c.name === selectedAnchor.name);
    if (direct) {
      return direct.value || direct.name || null;
    }
    return null;
  };

  // Breakdown counts by anchor
  const breakdown = useMemo(() => {
    const map = new Map();
    anchorOptionItems.forEach((o) => map.set(o.name, 0));
    let unassigned = 0;
    accessibleEmployees.forEach((emp) => {
      const val = getEmployeeAnchorValue(emp.id);
      if (!val) {
        unassigned += 1;
        return;
      }
      if (map.has(val)) {
        map.set(val, map.get(val) + 1);
      } else {
        // In case value exists but not in structure (edge), track under 'Other'
        map.set(val, 1);
      }
    });
    return { map, unassigned };
  }, [accessibleEmployees, anchorOptionItems, selectedAnchor]);

  const anchorNames = visibleCategories.map((c) => c.name);

  const loading = !get_survey_structure?.[0] || !visibleCategories?.length;

  return (
    <Page>
      <HeroBackdrop />
      <Header>
        <Breadcrumb>
          <CrumbLink to="/leader/audit/home">Home</CrumbLink>
          <CrumbSeparator>/</CrumbSeparator>
          <CrumbCurrent>Data access</CrumbCurrent>
        </Breadcrumb>
        <HeaderTitle>Data access</HeaderTitle>
        <BigStat aria-live="polite">
          {isAdmin
            ? "You are an admin and have access to all data."
            : `You have access to ${totalAccessible} respondents' data.`}
        </BigStat>
      </Header>

      <ContentCard>
        <TopBar>
          <TopBarLeft>
            <StatNumber aria-live="polite">
              {isAdmin ? activeMembers.length : totalAccessible}
            </StatNumber>
            <StatLabel>total respondents</StatLabel>
          </TopBarLeft>
          <TopBarRight>
            <Label>Breakdown by</Label>
            <Select
              value={selectedAnchorName}
              onChange={(e) => setSelectedAnchorName(e.target.value)}
              aria-label="Select anchor to view breakdown"
            >
              {anchorNames.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </Select>
          </TopBarRight>
        </TopBar>

        {loading ? (
          <EmptyState>Loading categories…</EmptyState>
        ) : (
          <BreakdownGrid>
            {Array.from(breakdown.map.entries())
              .filter(([_, count]) => count > 0)
              .map(([name, count], idx) => (
                <AnimatedTile key={name} delayMs={idx * 40}>
                  <BreakdownName>{name}</BreakdownName>
                  <BreakdownCount>{count}</BreakdownCount>
                </AnimatedTile>
              ))}
            {breakdown.unassigned > 0 && (
              <AnimatedTile $muted>
                <BreakdownName>Unassigned</BreakdownName>
                <BreakdownCount>{breakdown.unassigned}</BreakdownCount>
              </AnimatedTile>
            )}
          </BreakdownGrid>
        )}
      </ContentCard>
    </Page>
  );
};

export default DataAccess;

// Styles
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Page = styled.main`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 0 24px 24px;
  max-width: 1200px;
  margin: 0 auto;
  background: #fff;
  animation: ${fadeUp} 280ms ease-out both;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Header = styled.header`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 32px 0 10px;
  min-height: 120px;
`;

const HeaderTitle = styled.h1`
  margin: 0;
  font-size: 34px;
  font-weight: 700;
  color: #111111;
  letter-spacing: -0.02em;
  position: relative;
  &::after {
    content: "";
    position: absolute;
    left: 0;
    bottom: -10px;
    width: 64px;
    height: 3px;
    background: linear-gradient(90deg, #2c6bed, #6b5cff);
    border-radius: 2px;
    opacity: 0.9;
  }
`;

const HeaderSubtitle = styled.p`
  margin: 0;
  font-size: 14px;
  color: #6b7280;
`;

const Breadcrumb = styled.nav`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #6b7280;
`;

const CrumbLink = styled(Link)`
  color: #6b7280;
  text-decoration: none;
  &:hover { color: #374151; text-decoration: underline; }
`;

const CrumbSeparator = styled.span`
  color: #9ca3af;
`;

const CrumbCurrent = styled.span`
  color: #374151;
`;

const BigStat = styled.div`
  margin-top: 8px;
  font-size: 26px;
  line-height: 1.35;
  font-weight: 700;
  color: #111827;
`;

const ContentCard = styled.section`
  border: 1px solid #e7e7e7;
  border-radius: 10px;
  background: #ffffff;
  transition: box-shadow 240ms ease, transform 240ms ease, border-color 240ms ease;
  position: relative;
  overflow: hidden;
  &:hover {
    border-color: #dcdcdc;
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.06);
    transform: translateY(-1px);
  }
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: radial-gradient(1200px 400px at -10% -10%, rgba(44,107,237,0.06), transparent 60%);
    opacity: 0;
    transition: opacity 240ms ease;
  }
  &:hover::after { opacity: 1; }
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 20px;
  border-bottom: 1px solid #f0f2f5;
  flex-wrap: wrap;
`;

const TopBarLeft = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
`;

const StatNumber = styled.div`
  font-size: 28px;
  font-weight: 800;
  color: #111827;
`;

const StatLabel = styled.div`
  font-size: 13px;
  color: #6b7280;
`;

const TopBarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Label = styled.label`
  font-size: 13px;
  color: #374151;
`;

const Select = styled.select`
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 8px 12px;
  background: #ffffff;
  color: #111827;
  font-size: 14px;
  outline: none;
  transition: border-color 160ms ease, box-shadow 160ms ease;
  &:focus {
    border-color: #476dfa;
    box-shadow: 0 0 0 3px rgba(71, 109, 250, 0.12);
  }
`;

const BreakdownGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 12px;
  padding: 16px;
  @media (max-width: 1024px) {
    grid-template-columns: repeat(6, 1fr);
  }
  @media (max-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const BreakdownCard = styled.div`
  grid-column: span 3;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 14px;
  background: ${(p) => (p.$muted ? "#fafafa" : "#fff")};
  transition: transform 180ms ease, box-shadow 220ms ease, border-color 220ms ease;
  will-change: transform;
  &:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(0,0,0,0.06); }
`;

const BreakdownName = styled.div`
  font-size: 13px;
  color: #4b5563;
`;

const BreakdownCount = styled.div`
  margin-top: 4px;
  font-size: 20px;
  font-weight: 700;
  color: #111827;
`;

const EmptyState = styled.div`
  padding: 24px;
  font-size: 14px;
  color: #6b7280;
`;

const HeroBackdrop = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 50%;
  width: 100vw;
  transform: translateX(-50%);
  z-index: -1;
  pointer-events: none;
  background: radial-gradient(1200px 500px at 10% -10%, rgba(255, 182, 141, 0.28), transparent 60%),
              radial-gradient(1000px 420px at 90% -20%, rgba(123, 97, 255, 0.22), transparent 60%),
              radial-gradient(900px 380px at 50% -10%, rgba(44, 107, 237, 0.20), transparent 60%);
  filter: saturate(110%);
`;

const itemFade = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const AnimatedTile = styled(BreakdownCard)`
  animation: ${itemFade} 360ms ease-out both;
  animation-delay: ${(p) => (p.delayMs ? `${p.delayMs}ms` : "0ms")};
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;
